import styled from "@emotion/styled";
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";
import { DSButton } from "@hundred5/design-system";
import { Trans, useLingui } from "@lingui/react/macro";
import { Form, Formik, FormikErrors } from "formik";
import { isFilled, isValidEmail } from "../../utils/validation";
import { FormikTextField } from "../forms";
import { Icon } from "../icon";

interface EmailFormProps {
  sendEmail: (email: string) => Promise<any>;
  dismiss: () => void;
}

type TValues = {
  email: string;
};

export function EmailForm({ sendEmail, dismiss }: EmailFormProps) {
  const { t } = useLingui();
  const handleValidate = (values: TValues) => {
    const errors: FormikErrors<TValues> = {};

    if (!isFilled(values.email)) {
      errors.email = t`Enter your email address`;
    } else if (!isValidEmail(values.email)) {
      errors.email = t`Enter a valid email address`;
    }

    return errors;
  };

  const handleSubmit = async (values: TValues) => {
    sendEmail(values.email);
  };

  return (
    <Formik<TValues>
      initialValues={{ email: "" }}
      validate={handleValidate}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <FormContainer>
          <FormTitle>
            <Trans>Send this test to your e-mail</Trans>
          </FormTitle>

          <FormikTextField
            id="email"
            name="email"
            label={t`Email`}
            maxLength={200}
            required
          />

          <FormFooter>
            <DSButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? t`Sending...` : t`Send`}
            </DSButton>
            <DSButton variant="secondary" onClick={dismiss}>
              <Trans comment="Submit button">Continue anyway</Trans>
              <Icon icon={faArrowRight} />
            </DSButton>
          </FormFooter>
        </FormContainer>
      )}
    </Formik>
  );
}

const FormContainer = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex-grow: 1;
`;

const FormTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${(props) => props.theme.typography.colorPrimary};
`;

const FormFooter = styled.div`
  margin-top: auto;
  display: grid;
  gap: 8px;

  button {
    width: 100%;
  }
`;
